import React, { useState, useEffect } from "react";
import CountryAutocomplete from "../components/UI/countryAutocomplete";
import { getAllUsers, addUser, updateUser } from "../services/settings";
import { useNavigate } from "react-router-dom";
import Loader from "../components/UI/loader";
import Popup from "../components/UI/popup";
import { googleSignOut } from "../services/googleSignUp";
import { ref, getDatabase, get, update } from "firebase/database";
import { v4 } from "uuid";

const inputClass =
  "w-full px-4 py-3 bg-gray-50 rounded-xl border border-purple-200 outline-none text-sm text-gray-900 placeholder-gray-400 focus:border-primary";

function Profile() {
  const [userData, setUserData] = useState({ companyName: "", country: "", maskName: false });
  const [user, setUser] = useState();
  const [loading, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const email = localStorage.getItem("email");
  const navigate = useNavigate();

  useEffect(() => {
    getAllUsers(setLoader, setUser, email);
  }, []);

  useEffect(() => {
    if (user && user.length > 0) {
      handleAdd();
      const currentUser = user.find((u) => u.email === email);
      if (currentUser) {
        if (currentUser.id) {
          localStorage.setItem("ownerId", currentUser.id);
        } else {
          const newId = v4();
          localStorage.setItem("ownerId", newId);
          const db = getDatabase();
          get(ref(db, "userInfo")).then((snap) => {
            if (!snap.exists()) return;
            const allData = snap.val();
            const key = Object.keys(allData).find((k) => allData[k].email === email);
            if (key) update(ref(db, `userInfo/${key}`), { id: newId });
          });
        }
        setUserData({
          companyName: currentUser.companyName || "",
          country: currentUser.country || "",
          maskName: currentUser.maskName || false,
        });
      }
    }
  }, [user]);

  async function handleAdd() {
    const filteredData = user.filter((i) => i.email === email);
    if (filteredData.length === 0) {
      addUser(setLoader, setUser, email);
    }
  }

  function handleData(e) {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  }

  const isValid = userData.companyName && userData.country;

  function handleSubmit(e) {
    e.preventDefault();
    const currentUserData = user?.find((u) => u.email === email);
    const data = {
      companyName: userData.companyName,
      country: userData.country,
      maskName: userData.maskName,
      email,
    };
    updateUser(setLoader, data, email, setOpen, currentUserData?.id);
  }

  const onClose = () => {
    setOpen(false);
    navigate("/settings");
  };

  const handleLogout = async () => {
    await googleSignOut();
    localStorage.removeItem("email");
    navigate("/");
  };

  const initial = email?.charAt(0).toUpperCase() || "?";

  return (
    <div className="max-w-md mx-auto">
      {/* Header */}
      <div className="px-5 pt-6 pb-2 flex items-center gap-3">
        <button
          onClick={() => navigate("/settings")}
          className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="text-sm text-gray-500">Your account details</p>
        </div>
      </div>

      {/* Avatar + email */}
      <div className="flex flex-col items-center py-6 px-5">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
          <span className="text-2xl font-bold text-primary">{initial}</span>
        </div>
        <p className="text-sm text-gray-500">{email}</p>
      </div>

      <div className="mx-5 flex flex-col gap-y-3">
        {/* Account form */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Business Details</h2>
          <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Business Name</label>
              <input
                value={userData.companyName}
                name="companyName"
                className={inputClass}
                placeholder="Your business name"
                onChange={handleData}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Country</label>
              <CountryAutocomplete
                value={userData.country}
                name="country"
                className={inputClass}
                onChange={handleData}
                placeholder="Search your country…"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">Mask customer names</p>
                <p className="text-xs text-gray-500">
                  Show token number instead of name on public display
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUserData((prev) => ({ ...prev, maskName: !prev.maskName }))}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                  userData.maskName ? "bg-primary" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    userData.maskName ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <button
              disabled={!isValid}
              type="submit"
              className={`w-full py-3 rounded-xl font-semibold text-sm mt-1 ${
                isValid ? "bg-primary text-white" : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Save Changes
            </button>
          </form>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full py-3 bg-white border border-red-200 text-red-500 text-sm font-semibold rounded-xl"
        >
          Log out
        </button>

        <div className="mb-4" />
      </div>

      {open && (
        <Popup
          open={open}
          content="Profile updated successfully!"
          buttonContent="Okay"
          onClose={onClose}
        />
      )}
      {loading && <Loader loading={loading} />}
    </div>
  );
}

export default Profile;
